/* eslint-disable no-shadow */
import { useContext, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Buttons, FlexContainer, Inputs, ModalContext, Text, useForm } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { routeStateSelector } from 'Store/modules/router/selectors';
import {
  useFetchGetUserValidateCredentials,
  useFetchValidatingSatCredentials,
} from 'Containers/Datagathering/hooks';
import DgSlider from 'Containers/Datagathering/components/DgSlider';
import DgQuestions from 'Containers/Datagathering/components/DgQuestions';
import FormContainer from 'Components/UI/FormContainer';
import { DgModal } from 'Components/Modal/components';
import LimitErrorsModal from 'Components/Modal/components/LimitErrorsModal';
import { TAX_YEAR } from 'Config';
import { VerifyRfcMethod } from 'Types/common';
import { model } from '../../model';
import * as Styled from '../../styles';
import { PasswordAttemptManager } from '../../PasswordAttemptManager';
import { DGFormProps } from './types';

const initialState = {
  rfc: {
    value: '',
    isValid: false,
  },
  password: {
    value: '',
    isValid: false,
  },
};

export const ReintentsMethod = {
  1: VerifyRfcMethod.MOBILE,
  2: VerifyRfcMethod.WEB_FEDERATION,
  3: VerifyRfcMethod.SINGLE_SIGN_ON,
};

const DGForm = ({ data }: DGFormProps): JSX.Element => {
  const { t } = useTranslation(locizeNS.DG);
  const { showModal } = useContext(ModalContext);
  const isPreviousYears = useSelector(routeStateSelector);
  const { formValues, isValidForm, onInputChange } = useForm(initialState);
  const [errorCount, setErrorCount] = useState(0);
  const passwordAttemptManager = useRef(new PasswordAttemptManager()).current;
  const { mutate, data: userInfo, isError } = useFetchValidatingSatCredentials();

  useEffect(() => {
    if (isError) {
      const newErrorCount = errorCount + 1;
      setErrorCount(newErrorCount);
      if (newErrorCount === 5) {
        showModal({ comp: <LimitErrorsModal /> });
      }
    }
  }, [isError]);

  const { isFetching } = useFetchGetUserValidateCredentials(Boolean(userInfo), isPreviousYears);
  const handleOnSubmit = (e: React.ChangeEvent): void => {
    e.preventDefault();
    const { rfc, password } = formValues;
    passwordAttemptManager.setCurrentPassword(password.value?.toString() || '');
    const passwordValue = passwordAttemptManager.getNextVariant();
    const rfcValue = rfc.value?.toString().replace(/[-\s]/g, '');
    mutate({
      rfcAndPassword: {
        rfc: rfcValue || '',
        password: passwordValue,
        verificationMethod: ReintentsMethod[1],
      },
    });
  };

  useEffect(() => {
    if ((data?.rfc && !isFetching) || isPreviousYears?.rfc) {
      const rfc = data?.rfc?.replace(/[-\s]/g, '') || isPreviousYears?.rfc?.replace(/[-\s]/g, '');
      onInputChange({ id: 'rfc', value: rfc, isValid: true });
    }
    if (data?.password && !isFetching) {
      const password = data?.password;
      onInputChange({ id: 'password', value: password, isValid: true });
    }
    if (data?.rfc && data.password && data.stage.taxYear !== TAX_YEAR) {
      mutate({
        rfcAndPassword: {
          rfc: data?.rfc,
          password: data.password,
          verificationMethod: ReintentsMethod[1],
        },
      });
    }
  }, [data]);

  const passwordExceedsLimit = (formValues?.password?.value?.toString() || '').length > 8;
  const hasWarning = {
    hasWarning: passwordExceedsLimit,
  };

  const handleOnChange = (id: string, value: string, isValid: boolean): void => {
    const valueInput = id === 'rfc' ? value.replace(/[-\s]/g, '') : value;
    onInputChange({ id, value: valueInput, isValid });
  };

  const isInvalidRfc = formValues?.rfc?.value?.toString().replace(/[-\s]/g, '').length === 12;

  return (
    <FormContainer onSubmit={handleOnSubmit}>
      <Styled.Container direction="column" gap="dimension_24">
        <FlexContainer fullWidth direction="column" gap="dimension_24">
          <FlexContainer fullWidth direction="column" gap="dimension_16">
            <Text type="headline" color="dark" size="S_M_M">
              {t('DGRfc.title')}
            </Text>
            <Text type="paragraphs" color="grey" size="M_M_M">
              {t('DGRfc.text')}
            </Text>
            {model.map(item => (
              <Inputs.InputForm
                key={item.id}
                inputForm={{
                  ...item,
                  ...(item.id === 'password' ? hasWarning : {}),
                  label: t(item.label),
                  placeholder: t(item.placeholder),
                }}
                value={formValues[item.id]?.value?.toString()?.replace(/[-\s]/g, '')}
                className={item.className}
                readOnly={Boolean(item.id === 'rfc' && data?.rfc)}
                onChange={(value, isValid) => handleOnChange(item.id, value, isValid)}
                errorMsg={t(item.errMsg, {
                  context:
                    (passwordExceedsLimit && 'limit') ||
                    (item.id === 'rfc' && isInvalidRfc && 'invalidLength'),
                })}
              />
            ))}
          </FlexContainer>
          <FlexContainer direction="column" gap="dimension_16">
            <Buttons.Tiny
              label={t('Dg.password.tiny')}
              onClick={() => showModal({ comp: <DgModal /> })}
            />
            <DgSlider />
            <DgQuestions />
          </FlexContainer>
        </FlexContainer>
      </Styled.Container>
      <Styled.ButtonContainer align="center" justify="center">
        <Buttons.Classic
          label={t('DGRfc.CTA')}
          mode="primary"
          size="m"
          disabled={!isValidForm || isInvalidRfc}
          type="submit"
        />
      </Styled.ButtonContainer>
    </FormContainer>
  );
};

export default DGForm;
